import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { installHeadTags, rememberStaffPharmacy, staffPharmacyCode } from '../lib/pwa';
import { toast } from 'sonner';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

/** Staff Companion login — same staff account as desktop, no separate signup. */
export default function StaffAppLoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [params] = useSearchParams();
  const pharmacyCode = params.get('p') || staffPharmacyCode();
  const [pharmacy, setPharmacy] = useState(null);

  useEffect(() => installHeadTags(pharmacyCode), [pharmacyCode]);

  useEffect(() => {
    if (!pharmacyCode) return undefined;
    let live = true;
    api.get(`/staff-app/pharmacy/${encodeURIComponent(pharmacyCode)}`)
      .then((r) => { if (live) { setPharmacy(r.data); rememberStaffPharmacy(pharmacyCode, r.data.name); } })
      .catch(() => {});
    return () => { live = false; };
  }, [pharmacyCode]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate('/staff-app', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  if (user) return <Navigate to="/staff-app" replace />;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-5" data-testid="staff-app-login-page">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src={pharmacyCode ? `/api/staff-app/manifest/${encodeURIComponent(pharmacyCode)}/icon/192.png` : '/staff-icon-192.png'} alt="" className="h-16 w-16 rounded-2xl shadow-sm mb-4 object-contain bg-white"
            onError={(e) => { e.currentTarget.src = '/staff-icon-192.png'; }} />
          <h1 className="text-xl font-bold text-foreground tracking-tight">Staff Companion</h1>
          {pharmacy
            ? <p className="text-sm text-muted-foreground mt-1" data-testid="staff-app-login-pharmacy">{pharmacy.name}</p>
            : <p className="text-sm text-muted-foreground mt-1">Chats, orders and alerts, in your pocket</p>}
        </div>

        <form onSubmit={handleLogin} className="space-y-4 bg-card border border-border rounded-2xl p-5">
          <div className="space-y-1.5">
            <Label htmlFor="staff-app-email">Email</Label>
            <Input id="staff-app-email" type="email" autoComplete="username" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="staff-app-login-email" placeholder="you@pharmacy.com" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="staff-app-password">Password</Label>
            <Input id="staff-app-password" type="password" autoComplete="current-password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="staff-app-login-password" placeholder="Your staff password" />
          </div>
          <Button type="submit" className="w-full h-11 text-base" disabled={loading} data-testid="staff-app-login-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Sign In
          </Button>
        </form>

        <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground mt-6">
          <ShieldCheck className="h-3.5 w-3.5" /> Uses your existing DutiLyne staff account
        </p>
      </div>
    </div>
  );
}
